<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader />
  </div>
  <div v-else class="transactions-view">
    <MainHeader title="Transactions">
      <template #right>
        <div
          class="help-beacon"
          @click="openBeacon"
          title="Get Help"
          role="button"
          aria-label="Get Help"
        >
          <font-awesome-icon
            :icon="['fas', 'question']"
            class="help-beacon-icon"
          />
        </div>
      </template>
    </MainHeader>

    <div class="view">
      <div class="content-padding">
        <div class="flex-med-buttons mb-1">
          <button
            class="teal-button w-100 mb-1 mb-med-none"
            @click="router.push({ name: 'AddTransaction' })"
            role="button"
            aria-label="Add Transaction"
          >
            + Add Transaction
          </button>
          <button
            class="filter-toggle-btn w-100 mb-1 mb-med-none"
            :class="{ active: showFilterSection }"
            role="button"
            aria-label="Toggle Filters"
            @click="showFilterSection = !showFilterSection"
          >
            <font-awesome-icon
              :icon="['fas', 'sliders']"
              style="margin-right: 6px"
            />
            {{ showFilterSection ? 'Close Filters' : 'Filter Transactions' }}
          </button>
        </div>

        <transition name="filter-slide">
          <div v-if="showFilterSection" class="filter-card wrapper-card mb-2">
            <div class="filter-section-block">
              <span class="section-label">Search</span>
              <div class="search-row">
                <input
                  type="text"
                  class="input w-100"
                  v-model="searchText"
                  placeholder="Search transactions..."
                  aria-label="Search Transactions"
                />
              </div>
              <label class="filter-check-label mt-half">
                <input type="checkbox" v-model="showOnlyUncategorized" />
                Show uncategorized only
              </label>
            </div>

            <div class="filter-section-block">
              <span class="section-label">Filter by</span>
              <div class="filter-row-grid">
                <div class="filter-field">
                  <label class="filter-field-label">Category</label>
                  <select
                    class="input form-select filter-select"
                    v-model="parentCategory"
                    @change="selectedEditParentCategory"
                    :disabled="showOnlyUncategorized"
                  >
                    <option value="" selected disabled>All categories</option>
                    <option
                      v-for="cat in store.categories"
                      :key="cat.categoryID"
                      :value="cat"
                    >
                      {{ cat.name }}
                    </option>
                  </select>
                </div>
                <div class="filter-field">
                  <label class="filter-field-label">Subcategory</label>
                  <select
                    class="input form-select filter-select"
                    v-model="selectedSubcategory"
                    :disabled="showOnlyUncategorized"
                  >
                    <option value="" selected disabled>All</option>
                    <option
                      v-for="sub in parentCategory?.subcategories || []"
                      :key="sub.categoryID"
                      :value="sub"
                    >
                      {{ sub.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="filter-field mt-half">
                <label class="filter-field-label">Date range</label>
                <select
                  class="input form-select filter-select"
                  v-model="selectedDate"
                >
                  <option value="" selected disabled>Select date</option>
                  <option v-for="opt in dateOptions" :key="opt" :value="opt">
                    {{ opt }}
                  </option>
                </select>
              </div>
              <div
                v-if="selectedDate === 'Custom'"
                class="filter-row-grid mt-half"
              >
                <div class="filter-field">
                  <label class="filter-field-label">Start date</label>
                  <input
                    v-model="startDate"
                    type="date"
                    class="input date-picker"
                  />
                </div>
                <div class="filter-field">
                  <label class="filter-field-label">End date</label>
                  <input
                    v-model="endDate"
                    type="date"
                    class="input date-picker"
                  />
                </div>
              </div>
            </div>

            <div class="filter-actions">
              <button
                class="teal-outline-button w-100"
                @click="applyFilters()"
                aria-label="Apply Filters"
              >
                Apply
              </button>
              <button
                class="red-outline-button w-100"
                @click="resetFilters()"
                aria-label="Reset Filters"
              >
                Reset
              </button>
            </div>
          </div>
        </transition>

        <button
          class="transparent-button small-grey-text mb-half"
          @click="toggleBulkMode"
        >
          {{ bulkMode ? 'Cancel' : 'Select Multiple' }}
        </button>

        <div v-if="bulkMode" class="mb-2">
          <button
            class="teal-button w-100 mb-1"
            @click="showRecategorizeModal"
            aria-label="Recategorize selected transactions"
          >
            Recategorize selected
          </button>
          <label class="filter-check-label">
            <input
              type="checkbox"
              :checked="allSelected"
              @change="(e) => toggleSelectAll(e.target.checked)"
            />
            Select all
          </label>
        </div>

        <div v-if="!filteredTransactions || filteredTransactions.length === 0">
          <EmptyState
            :title="
              premiumUser
                ? 'No transactions this month'
                : 'Start tracking your spending'
            "
            :subtitle="
              premiumUser
                ? 'Your bank is connected — transactions sync automatically. Check back soon or add one manually.'
                : 'Add your first transaction manually to start building your financial picture.'
            "
          >
            <template #icon>
              <svg v-if="premiumUser" viewBox="0 0 72 72" fill="none">
                <rect
                  x="4"
                  y="4"
                  width="64"
                  height="64"
                  rx="16"
                  fill="#EEEDFE"
                />
                <rect
                  x="16"
                  y="26"
                  width="40"
                  height="6"
                  rx="3"
                  fill="#AFA9EC"
                  opacity="0.5"
                />
                <rect
                  x="16"
                  y="36"
                  width="28"
                  height="6"
                  rx="3"
                  fill="#AFA9EC"
                  opacity="0.35"
                />
                <rect
                  x="16"
                  y="46"
                  width="34"
                  height="6"
                  rx="3"
                  fill="#AFA9EC"
                  opacity="0.2"
                />
                <circle cx="54" cy="20" r="10" fill="#7F77DD" />
                <path
                  d="M50 20l3 3 5-5"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <svg v-else viewBox="0 0 72 72" fill="none">
                <rect
                  x="4"
                  y="4"
                  width="64"
                  height="64"
                  rx="16"
                  fill="#E1F5EE"
                />
                <rect
                  x="16"
                  y="26"
                  width="40"
                  height="6"
                  rx="3"
                  fill="#9FE1CB"
                  opacity="0.6"
                />
                <rect
                  x="16"
                  y="36"
                  width="28"
                  height="6"
                  rx="3"
                  fill="#9FE1CB"
                  opacity="0.4"
                />
                <rect
                  x="16"
                  y="46"
                  width="34"
                  height="6"
                  rx="3"
                  fill="#9FE1CB"
                  opacity="0.25"
                />
                <circle cx="54" cy="20" r="10" fill="#5DCAA5" />
                <path
                  d="M54 16v8M50 20h8"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </template>
            <template #actions>
              <button
                class="gradient-button w-100"
                @click="router.push({ name: 'AddTransaction' })"
              >
                +
                {{
                  premiumUser ? 'Add a transaction' : 'Add my first transaction'
                }}
              </button>
              <button
                v-if="premiumUser"
                class="outline-button w-100"
                @click="router.push({ name: 'ConnectedAccounts' })"
              >
                Go to connected accounts
              </button>
              <button
                v-else
                class="outline-button w-100"
                @click="router.push({ name: 'Paywall' })"
              >
                Upgrade to Premium
              </button>
            </template>
          </EmptyState>
        </div>

        <div v-else class="transactions-list-wrapper">
          <div
            v-for="item in filteredTransactions"
            :key="item.userTransactionID"
            class="transaction-row-container"
          >
            <div v-if="bulkMode" class="item-checkbox-cell">
              <input
                type="checkbox"
                :value="item"
                v-model="selectedTransactions"
                aria-label="Select for bulk recategorization"
              />
            </div>
            <div class="transactions-list" @click="editTransaction(item)">
              <div class="transaction-icon">
                <div class="transaction-icon-wrapper">
                  <span class="icon-wrapper">
                    <font-awesome-icon
                      v-if="item.transactionSubcategory"
                      :icon="
                        item.transactionSubcategory?.icon
                          ? parseIconString(item.transactionSubcategory.icon)
                          : 'fa-solid fa-tag'
                      "
                      :style="{
                        color: item.transactionCategory?.iconColor || '#86c24c',
                      }"
                      class="subcategory-icon"
                      aria-label="transaction subcategory icon"
                    />
                    <font-awesome-icon
                      v-else
                      :icon="
                        item.transactionCategory?.icon
                          ? parseIconString(item.transactionCategory.icon)
                          : 'fa-solid fa-tag'
                      "
                      :style="{
                        color: item.transactionCategory?.iconColor || '#86c24c',
                      }"
                      class="subcategory-icon"
                      aria-label="transaction category icon"
                    />
                  </span>
                </div>
              </div>
              <div class="displayName">
                {{ item.displayName }}
                <span v-if="item.isSplit" class="split-badge">
                  <font-awesome-icon :icon="['fas', 'code-fork']" /> Split
                </span>
                <span
                  v-else-if="item.parentTransactionID"
                  class="split-badge split-badge-child"
                >
                  Part of split
                </span>
              </div>
              <div
                class="amount text-right"
                :class="item.isIncome ? 'amount-income' : 'amount-expense'"
              >
                {{ formatMoney(item.amount) }}
              </div>
              <div class="transaction-grey institution">
                <div>
                  {{
                    item.transactionSubcategory?.name ||
                    item.transactionCategory?.name ||
                    'Uncategorized'
                  }}
                </div>
                <div>{{ item.institution }}</div>
              </div>
              <div class="text-right transaction-grey date">
                {{ formatDateRelative(item.transactionDate) }}
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="filteredTransactions && filteredTransactions.length > 0"
          class="totals-footer"
          role="status"
          aria-live="polite"
        >
          <div class="totals-row">
            <div class="totals-label-wrapper">
              <span class="totals-label">Total</span>
              <InfoTooltip
                position="top"
                aria-label="What does total mean?"
                text="Net total of all transactions shown above — expenses minus income. Transfers between your accounts are excluded so they don't double-count. Change your filters to see a different range."
              />
            </div>
            <div
              class="totals-amount"
              :class="{ 'income-total': totalExpenseAbs < 0 }"
            >
              {{ formatMoney(Math.abs(totalExpenseAbs)) }}
            </div>
          </div>

          <!-- <div class="totals-row">
            <div class="totals-label">Total</div>
            <div
              class="totals-amount"
              :class="{ 'income-total': totalExpenseAbs < 0 }"
            >
              {{ formatMoney(Math.abs(totalExpenseAbs)) }}
            </div>
          </div> -->
        </div>
      </div>
    </div>
  </div>

  <Modal ref="bulkCategorizeModal">
    <template #header>
      <h2>Recategorize transactions</h2>
    </template>
    <template #body>
      <p>
        You are about to recategorize {{ selectedTransactions.length }} selected
        transactions.
      </p>
      <p class="mt-half">
        This will apply the chosen category and subcategory to all selected
        transactions.
      </p>
      <div class="mb-2 mt-1">
        <select
          class="mb-1 form-select white-form-select form-select-box-shadow"
          v-model="bulkCategorizeCategory"
          @change="selectedBulkCategorizeCategory"
        >
          <option value="" selected disabled>Category</option>
          <option
            v-for="cat in store.categories"
            :key="cat.categoryID"
            :value="cat"
          >
            {{ cat.name }}
          </option>
        </select>
        <select
          v-if="bulkCategorizeCategory?.subcategories"
          class="form-select white-form-select form-select-box-shadow"
          v-model="bulkSubcategory"
        >
          <option value="" selected disabled>Subcategory</option>
          <option
            v-for="sub in bulkCategorizeCategory.subcategories"
            :key="sub.categoryID"
            :value="sub"
          >
            {{ sub.name }}
          </option>
        </select>
      </div>
    </template>
    <template #footer>
      <div class="modal-footer-buttons">
        <button class="teal-outline-button" @click="closeRecategorizeModal">
          Cancel
        </button>
        <button
          class="teal-button"
          :disabled="!bulkCategorizeCategory"
          @click="submitBulkRecategorize"
        >
          Apply category
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
import { useMyStore } from '../store';
import { onMounted, ref, onBeforeUnmount, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { formatMoney } from '../utils/formatMoney.js';
import { formatDateRelative } from '../utils/formatDate.js';
import MainHeader from '../components/View-Header.vue';
import EmptyState from '../components/EmptyState.vue';
import { MonthsData } from '../utils/constants.js';
import SkeletonLoader from '../components/SkeletonLoader.vue';
import Modal from '../components/Main-Modal.vue';
import InfoTooltip from '../components/InfoTooltip.vue';

export default {
  name: 'AccountTransactions',
  components: { MainHeader, SkeletonLoader, Modal, EmptyState, InfoTooltip },
  setup() {
    const store = useMyStore();
    const router = useRouter();

    const loading = ref(true);
    const showFilterSection = ref(false);
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const monthsData = MonthsData;
    const selectedYear = ref(currentYear);
    const selectedMonth = ref(
      monthsData.find((month) => month.number - 1 === currentMonth)
    );
    const dateOptions = ref(['Current Month', 'Today', 'This Week', 'Custom']);

    const allTransactions = ref([]);
    const filteredTransactions = ref([]);
    const originalList = ref([]);
    const bulkMode = ref(false);
    const selectedTransactions = ref([]);
    const bulkCategorizeModal = ref(null);
    const bulkCategorizeCategory = ref('');
    const bulkSubcategory = ref('');

    const premiumUser = computed(() => store.user.premiumSubscription);

    const allSelected = computed(
      () =>
        filteredTransactions.value.length > 0 &&
        selectedTransactions.value.length === filteredTransactions.value.length
    );

    const totalExpenseAbs = computed(() =>
      filteredTransactions.value.reduce((sum, t) => {
        if (t.markedAsTransfer || t.isSplit) return sum;
        const amount = Math.abs(t.amount ?? 0);
        return t.isIncome ? sum - amount : sum + amount;
      }, 0)
    );

    const toggleBulkMode = () => {
      if (bulkMode.value) {
        cancelBulkRecategorize();
      } else {
        bulkMode.value = true;
      }
    };

    const selectedBulkCategorizeCategory = () => {
      bulkSubcategory.value = '';
    };

    const toggleSelectAll = (checked) => {
      selectedTransactions.value = checked
        ? [...filteredTransactions.value]
        : [];
    };

    const selectedEditParentCategory = () => {
      store.filters.selectedSubcategory = '';
    };

    const searchText = computed({
      get: () => store.filters.searchText,
      set: (val) => (store.filters.searchText = val),
    });
    const parentCategory = computed({
      get: () => store.filters.parentCategory,
      set: (val) => (store.filters.parentCategory = val),
    });
    const selectedSubcategory = computed({
      get: () => store.filters.selectedSubcategory,
      set: (val) => (store.filters.selectedSubcategory = val),
    });
    const selectedDate = computed({
      get: () => store.filters.selectedDate || 'Current Month',
      set: (val) => (store.filters.selectedDate = val),
    });
    const startDate = computed({
      get: () => store.filters.startDate,
      set: (val) => (store.filters.startDate = val),
    });
    const endDate = computed({
      get: () => store.filters.endDate,
      set: (val) => (store.filters.endDate = val),
    });
    const showOnlyUncategorized = computed({
      get: () => store.filters.showOnlyUncategorized,
      set: (val) => (store.filters.showOnlyUncategorized = val),
    });

    const parseIconString = (iconString) => {
      if (iconString) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return null;
    };

    const cancelBulkRecategorize = () => {
      selectedTransactions.value = [];
      bulkMode.value = false;
      closeRecategorizeModal();
    };

    const editTransaction = (item) => {
      // store.updateEditTransaction(item);
      router.push({
        name: 'EditTransaction',
        params: { userTransactionID: item.userTransactionID },
      });
    };

    const fetchCurrentMonthTransactions = () => {
      loading.value = true;
      const transactionsRequest = {
        StartDate: `${selectedYear.value}-${selectedMonth.value.number.toString().padStart(2, '0')}-${selectedMonth.value.firstDay.toString().padStart(2, '0')}`,
        EndDate: `${selectedYear.value}-${selectedMonth.value.number.toString().padStart(2, '0')}-${selectedMonth.value.lastDay.toString().padStart(2, '0')}`,
      };
      store.getTransactions(transactionsRequest).then((response) => {
        const sorted = response.data.sort(
          (a, b) => new Date(b.transactionDate) - new Date(a.transactionDate)
        );
        allTransactions.value = sorted;
        originalList.value = [...sorted];
        filteredTransactions.value = [...sorted];
        loading.value = false;
        if (
          store.filters.selectedDate === 'Custom' &&
          store.filters.startDate &&
          store.filters.endDate
        ) {
          applyFilters();
        } else {
          filterTransactionsList();
        }
      });
    };

    const submitBulkRecategorize = async () => {
      try {
        loading.value = true;
        bulkCategorizeModal.value?.closeModal();
        const payload = {
          transactionIDs: selectedTransactions.value.map(
            (t) => t.userTransactionID
          ),
          newCategoryID: bulkCategorizeCategory.value?.categoryID || null,
          newSubcategoryID: bulkSubcategory.value?.subcategoryID || null,
        };
        const success = await store.recategorizeTransactions(payload);
        if (success) {
          fetchCurrentMonthTransactions();
          cancelBulkRecategorize();
        } else {
          console.error('Failed to recategorize transactions');
        }
      } catch (error) {
        console.error('Error recategorizing transactions:', error);
      } finally {
        loading.value = false;
      }
    };

    const closeRecategorizeModal = () => {
      bulkCategorizeModal.value?.closeModal();
      bulkCategorizeCategory.value = '';
      bulkSubcategory.value = '';
    };

    const applyFilters = () => {
      loading.value = true;
      if (selectedDate.value === 'Custom') {
        if (!startDate.value || !endDate.value) {
          loading.value = false;
          return;
        }
        store
          .getTransactions({
            StartDate: startDate.value,
            EndDate: endDate.value,
          })
          .then((response) => {
            const sorted = response.data.sort(
              (a, b) =>
                new Date(b.transactionDate) - new Date(a.transactionDate)
            );
            originalList.value = sorted;
            filterTransactionsList();
          });
      } else {
        originalList.value = [...allTransactions.value];
        filterTransactionsList();
      }
    };

    const filterTransactionsList = () => {
      filteredTransactions.value = originalList.value.filter((transaction) => {
        let matchesSearch = true;
        let matchesCategory = true;
        let matchesSubcategory = true;
        let matchesDate = true;
        let matchesUncategorized = true;

        if (searchText.value) {
          matchesSearch = transaction.displayName
            .toLowerCase()
            .includes(searchText.value.toLowerCase());
        }
        if (parentCategory.value) {
          matchesCategory =
            transaction.transactionCategory?.categoryID ===
            parentCategory.value.categoryID;
        }
        if (selectedSubcategory.value) {
          matchesSubcategory =
            transaction.transactionSubcategory?.subcategoryID ===
            selectedSubcategory.value.subcategoryID;
        }

        const transactionDate = new Date(transaction.transactionDate);
        if (selectedDate.value === 'Current Month') {
          matchesDate =
            transactionDate.getMonth() === currentMonth &&
            transactionDate.getFullYear() === currentYear;
        } else if (selectedDate.value === 'Today') {
          matchesDate =
            transactionDate.getDate() === currentDate.getDate() &&
            transactionDate.getMonth() === currentDate.getMonth() &&
            transactionDate.getFullYear() === currentDate.getFullYear();
        } else if (selectedDate.value === 'This Week') {
          const firstDayOfWeek = new Date(currentDate);
          firstDayOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
          firstDayOfWeek.setHours(0, 0, 0, 0);
          const lastDayOfWeek = new Date(firstDayOfWeek);
          lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
          lastDayOfWeek.setHours(23, 59, 59, 999);
          matchesDate =
            transactionDate >= firstDayOfWeek &&
            transactionDate <= lastDayOfWeek;
        }

        if (showOnlyUncategorized.value) {
          matchesUncategorized =
            !transaction.isSplit &&
            (!transaction.transactionCategory ||
              !transaction.transactionCategory.name) &&
            (!transaction.transactionSubcategory ||
              !transaction.transactionSubcategory.name);
        }

        return (
          matchesSearch &&
          matchesCategory &&
          matchesSubcategory &&
          matchesDate &&
          matchesUncategorized
        );
      });
      loading.value = false;
    };

    const resetFilters = () => {
      store.filters.searchText = '';
      store.filters.parentCategory = '';
      store.filters.selectedSubcategory = '';
      store.filters.selectedDate = 'Current Month';
      store.filters.startDate = '';
      store.filters.endDate = '';
      store.filters.showOnlyUncategorized = false;
      originalList.value = [...allTransactions.value];
      filteredTransactions.value = [...allTransactions.value];
    };

    const showRecategorizeModal = () => {
      bulkCategorizeModal.value?.openModal();
    };

    const openBeacon = () => {
      if (window.Beacon && typeof window.Beacon === 'function') {
        window.Beacon('on', 'ready', () => {
          window.Beacon('open');
          window.Beacon('navigate', '/docs/search?query=transactions');
        });
        window.Beacon('open');
        window.Beacon('navigate', '/docs/search?query=transactions');
      } else {
        console.error('Beacon is not initialized');
      }
    };

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) mainElement.scrollTo(0, 0);
      }, 100);
      fetchCurrentMonthTransactions();
    });

    watch(
      () => [selectedDate.value, startDate.value, endDate.value],
      () => {
        if (selectedDate.value !== 'Custom') filterTransactionsList();
      }
    );

    watch(showOnlyUncategorized, (newValue) => {
      if (newValue) {
        store.filters.parentCategory = '';
        store.filters.selectedSubcategory = '';
      }
    });

    onBeforeUnmount(() => {
      window.Beacon('close');
    });

    return {
      store,
      loading,
      showFilterSection,
      currentDate,
      monthsData,
      selectedYear,
      selectedMonth,
      dateOptions,
      allTransactions,
      filteredTransactions,
      bulkMode,
      selectedTransactions,
      bulkCategorizeCategory,
      bulkSubcategory,
      totalExpenseAbs,
      searchText,
      parentCategory,
      selectedSubcategory,
      selectedDate,
      startDate,
      endDate,
      showOnlyUncategorized,
      router,
      allSelected,
      bulkCategorizeModal,
      premiumUser,
      editTransaction,
      formatMoney,
      formatDateRelative,
      applyFilters,
      resetFilters,
      parseIconString,
      selectedEditParentCategory,
      toggleSelectAll,
      showRecategorizeModal,
      closeRecategorizeModal,
      selectedBulkCategorizeCategory,
      cancelBulkRecategorize,
      toggleBulkMode,
      submitBulkRecategorize,
      openBeacon,
    };
  },
};
</script>

<style scoped>
.help-beacon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: var(--shadow-purple);
  transition:
    transform var(--transition-fast),
    box-shadow var(--transition-fast);
}
.help-beacon:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-purple-lg);
}
.help-beacon-icon {
  color: white;
  font-size: 13px;
}

.filter-toggle-btn {
  border: 2px solid var(--color-accent-purple);
  background: white;
  color: var(--color-accent-purple);
  border-radius: var(--radius-pill);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background var(--transition-fast),
    transform var(--transition-fast);
}
.filter-toggle-btn:hover,
.filter-toggle-btn.active {
  background: var(--purple-50);
  transform: translateY(-1px);
}
.dark-theme .filter-toggle-btn {
  background: transparent;
  color: var(--color-teal-mint);
  border-color: var(--color-teal-mint);
}

.filter-card {
  padding: 1.25rem;
}
.filter-section-block {
  margin-bottom: 1.25rem;
}
.filter-section-block:last-of-type {
  margin-bottom: 0;
}
.filter-row-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.filter-field-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #555;
}
.dark-theme .filter-field-label {
  color: #aab;
}
.filter-check-label {
  font-size: 0.78rem;
  font-weight: 500;
  color: #555;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.dark-theme .filter-check-label {
  color: #aab;
}
.filter-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
}
.dark-theme .filter-actions {
  border-top-color: #2a3a4e;
}

.filter-slide-enter-active,
.filter-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.34, 1.2, 0.64, 1);
  overflow: hidden;
}
.filter-slide-enter-from,
.filter-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
  max-height: 0;
}
.filter-slide-enter-to,
.filter-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 600px;
}

.dark-theme .transactions-view .icon-wrapper:before,
.dark-theme .transactions-view .sub-icon-wrapper:before {
  background: #212e3e;
}

.transactions-list {
  display: grid;
  grid-template-columns: 15% 55% 30%;
  cursor: pointer;
  padding: 0.85rem 1rem;
  border-radius: var(--radius-md);
  margin-bottom: 0.5em;
  flex: 1 1 auto;
  transition:
    transform var(--transition-fast),
    box-shadow var(--transition-fast);
}
.light-theme .transactions-list {
  background: white;
  box-shadow: var(--shadow-sm);
  border: 1px solid #f0f0f0;
}
.light-theme .transactions-list:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}
.dark-theme .transactions-list {
  background: #212e3e;
  border: 1px solid #2a3a4e;
}
.dark-theme .transactions-list:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.25);
}

.transaction-icon {
  grid-row: 1 / span 2;
  display: flex;
  justify-content: start;
  align-items: center;
}

.transaction-icon-wrapper {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  flex-shrink: 0;
}
.dark-theme .icon-wrapper {
  background: #1a2535;
}

.subcategory-icon {
  font-size: 15px;
}

.transaction-icon svg {
  display: block;
  position: relative;
  text-align: center;
  line-height: 30px;
}

.transactions-list .displayName {
  font-size: 0.9rem;
  font-weight: 600;
  min-width: 0;
  white-space: normal;
  overflow-wrap: break-word;
}
.light-theme .transactions-list .displayName {
  color: #515a65;
}
.dark-theme .transactions-list .displayName {
  color: #ffffff;
}

.split-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #e1f5ee;
  color: #0f6e56;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 20px;
  margin-left: 6px;
  vertical-align: middle;
  white-space: nowrap;
}
.split-badge-child {
  background: #eef0fb;
  color: #4a4286;
}
.dark-theme .split-badge {
  background: #15302a;
  color: #7fdcb9;
}
.dark-theme .split-badge-child {
  background: #1f2342;
  color: #aaa3f0;
}

.institution {
  font-size: 0.78em;
}
.date {
  font-size: 0.78em;
}

.transactions-list .amount {
  border-radius: 100px;
  border: none;
  font-weight: 600;
  font-size: 0.95em;
}

.light-theme .transaction-grey {
  color: #515a65;
}
.dark-theme .transaction-grey {
  color: #d4d2d2;
}

.transaction-row-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 0.5em;
}
.item-checkbox-cell {
  flex: 0 0 auto;
}

.transparent-button {
  background: transparent;
  border: transparent;
  padding: 0.4em 0;
  font-size: 0.85rem;
}
.mb-half {
  margin-bottom: 0.4rem;
}

.totals-footer {
  position: sticky;
  bottom: 0;
  z-index: 5;
  padding: 0.85rem 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(6px);
  margin-top: 0.5rem;
  border-radius: var(--radius-md) var(--radius-md) 0 0;
}
.light-theme .totals-footer {
  background: rgba(255, 255, 255, 0.95);
}
.dark-theme .totals-footer {
  background: rgba(33, 46, 62, 0.95);
}

.totals-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 700;
  font-size: 1em;
}
.totals-label {
  color: #888;
  font-weight: 600;
  font-size: 0.85rem;
}
.totals-amount {
  text-align: right;
  color: var(--color-primary);
}
.light-theme .income-total {
  color: #86c24c;
}
.dark-theme .income-total {
  color: #89ca50;
}

.dark-theme .filter-select {
  border: 1px solid #212e3e;
  color: #fff;
  background-color: #171e29;
}

.icon-wrapper svg {
  display: block;
  position: relative;
}

@media screen and (min-width: 992px) {
  .icon-wrapper svg {
    line-height: 50px;
  }
}
</style>
